import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../services/supabase';
import { MODULES } from '../../config/moduleDefs';
import { Card, Button, Badge, LoadingSkeleton } from '../ui/UIComponents';
import { BarChart3, CheckCircle, ChevronDown, Clock, AlertTriangle, Users } from 'lucide-react';

const EXPANDED_SECTIONS_KEY = 'staffhub.dashboard-expanded-sections.v1';

const Dashboard = ({ onNavigate, staffList = [] }) => {
  const [stats, setStats] = useState({});
  const [uniqueClientTotal, setUniqueClientTotal] = useState(0);
  const [globalDueStats, setGlobalDueStats] = useState({ overdue: 0, thisWeek: 0, in2Weeks: 0, thisMonth: 0, nextMonth: 0, next2Months: 0 });
  const [staffWorkloads, setStaffWorkloads] = useState([]);
  const [unassignedWorkItems, setUnassignedWorkItems] = useState(0);
  const [activeStaffRow, setActiveStaffRow] = useState(null);
  const [activeSummaryCard, setActiveSummaryCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState(() => {
    try {
      const saved = localStorage.getItem(EXPANDED_SECTIONS_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return { topLevel: true, timeHorizon: true, ...parsed };
      }
    } catch {}
    return { topLevel: true, timeHorizon: true };
  });

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(EXPANDED_SECTIONS_KEY, JSON.stringify(expandedSections));
    } catch {}
  }, [expandedSections]);

  const fetchDashboardStats = async () => {
    setLoading(true);
    const newStats = {};
    let nextUniqueClientTotal = 0;
    let nextGlobalOverdue = 0;
    let nextGlobalThisWeek = 0;
    let nextGlobalIn2Weeks = 0;
    let nextGlobalThisMonth = 0;
    let nextGlobalNextMonth = 0;
    let nextGlobalNext2Months = 0;
    let nextUnassignedWorkItems = 0;
    const nextStaffWorkloadsMap = new Map();

    try {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const endOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
      const getFridayEnd = (date) => {
        const base = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        const day = base.getDay(); // 0=Sun ... 5=Fri
        const delta = (5 - day + 7) % 7;
        const friday = new Date(base);
        friday.setDate(base.getDate() + delta);
        return endOfDay(friday);
      };

      const weekEndFriday = getFridayEnd(today);
      const nextWeekEndFriday = (() => {
        const date = new Date(weekEndFriday);
        date.setDate(date.getDate() + 7);
        return endOfDay(date);
      })();

      const thisMonthEnd = endOfDay(new Date(today.getFullYear(), today.getMonth() + 1, 0));
      const nextMonthStart = new Date(today.getFullYear(), today.getMonth() + 1, 1);
      const nextMonthEnd = endOfDay(new Date(today.getFullYear(), today.getMonth() + 2, 0));
      const monthAfterNextStart = new Date(today.getFullYear(), today.getMonth() + 2, 1);
      const monthAfterNextEnd = endOfDay(new Date(today.getFullYear(), today.getMonth() + 3, 0));

      const getStaffName = (staffId) => {
        const match = (staffList || []).find(member => String(member.id) === String(staffId));
        return match?.name || match?.full_name || match?.email || `Staff ${staffId}`;
      };

      const ensureStaffWorkload = (staffId) => {
        const key = String(staffId);
        if (!nextStaffWorkloadsMap.has(key)) {
          nextStaffWorkloadsMap.set(key, {
            staffId: key,
            staffName: getStaffName(staffId),
            totalAssigned: 0,
            overdue: 0,
            thisWeek: 0,
            thisMonth: 0,
            nextMonth: 0,
            in2Months: 0,
            noDueDate: 0,
            moduleMap: new Map(),
          });
        }

        return nextStaffWorkloadsMap.get(key);
      };

      const hasDueInRange = (client, workstreams = [], start, end, { includeStart = true, includeEnd = true } = {}) => (
        workstreams.some((ws) => {
          const dueDate = client[ws.dueField];
          if (!dueDate) return false;
          const due = new Date(dueDate);
          if (Number.isNaN(due.getTime())) return false;

          const startOk = includeStart ? due >= start : due > start;
          const endOk = includeEnd ? due <= end : due < end;
          return startOk && endOk;
        })
      );

      const baseClientTables = [
        'clients_coy',
        'clients_charity',
        'clients_individual',
        'clients_unregistered',
        'clients_partnership',
      ];

      const baseClientModuleIds = new Set(['ch_company', 'charity', 'individual', 'unregistered', 'partnership']);

      for (const tableName of baseClientTables) {
        const { count, error } = await supabase
          .from(tableName)
          .select('id', { count: 'exact', head: true });

        if (error) {
          console.error(`Error fetching unique count from ${tableName}:`, error);
          continue;
        }

        nextUniqueClientTotal += Number(count || 0);
      }

      // Fetch stats for each module
      for (const [key, module] of Object.entries(MODULES)) {
        if (module.tableName) { // Include all modules with a table
          try {
            const { data, error } = await supabase
              .from(module.tableName)
              .select('*');

            if (error) {
              console.error(`Error fetching ${module.tableName}:`, error);
              continue;
            }

            const scopedData = typeof module.postFetchFilter === 'function'
              ? data.filter(client => {
                  try {
                    return module.postFetchFilter(client);
                  } catch {
                    return true;
                  }
                })
              : data;

            const total = scopedData.length;
            const overdue = scopedData.filter(client => {
              // Check if any due date is overdue
              return (module.workstreams || []).some(ws => {
                const dueDate = client[ws.dueField];
                return dueDate && new Date(dueDate) < today;
              });
            }).length;

            const thisWeek = scopedData.filter(client => {
              return hasDueInRange(client, module.workstreams || [], today, weekEndFriday);
            }).length;

            const in2Weeks = scopedData.filter(client => {
              return hasDueInRange(client, module.workstreams || [], weekEndFriday, nextWeekEndFriday, { includeStart: false, includeEnd: true });
            }).length;

            const thisMonth = scopedData.filter(client => {
              return hasDueInRange(client, module.workstreams || [], today, thisMonthEnd);
            }).length;

            const nextMonth = scopedData.filter(client => {
              return hasDueInRange(client, module.workstreams || [], nextMonthStart, nextMonthEnd);
            }).length;

            const next2Months = scopedData.filter(client => {
              return hasDueInRange(client, module.workstreams || [], monthAfterNextStart, monthAfterNextEnd);
            }).length;

            (module.workstreams || []).forEach((workstream) => {
              if (!workstream?.staffField) return;

              scopedData.forEach((client) => {
                const assignedStaffId = client[workstream.staffField];
                const dueRaw = workstream.dueField ? client[workstream.dueField] : null;
                const dueDate = dueRaw ? new Date(dueRaw) : null;
                const hasValidDue = Boolean(dueDate && !Number.isNaN(dueDate.getTime()));

                if (!assignedStaffId) {
                  nextUnassignedWorkItems += 1;
                  return;
                }

                const bucket = ensureStaffWorkload(assignedStaffId);
                bucket.totalAssigned += 1;
                const moduleEntry = bucket.moduleMap.get(module.id) || { moduleId: module.id, label: module.label, count: 0 };
                moduleEntry.count += 1;
                bucket.moduleMap.set(module.id, moduleEntry);

                if (!hasValidDue) {
                  bucket.noDueDate += 1;
                  return;
                }

                if (dueDate < today) bucket.overdue += 1;
                if (dueDate >= today && dueDate <= weekEndFriday) bucket.thisWeek += 1;
                if (dueDate >= today && dueDate <= thisMonthEnd) bucket.thisMonth += 1;
                if (dueDate >= nextMonthStart && dueDate <= nextMonthEnd) bucket.nextMonth += 1;
                if (dueDate >= monthAfterNextStart && dueDate <= monthAfterNextEnd) bucket.in2Months += 1;
              });
            });

            newStats[key] = { total, overdue, thisWeek, in2Weeks, thisMonth, nextMonth, next2Months };

            if (baseClientModuleIds.has(module.id)) {
              nextGlobalOverdue += overdue;
              nextGlobalThisWeek += thisWeek;
              nextGlobalIn2Weeks += in2Weeks;
              nextGlobalThisMonth += thisMonth;
              nextGlobalNextMonth += nextMonth;
              nextGlobalNext2Months += next2Months;
            }
          } catch (err) {
            console.error(`Error processing ${key}:`, err);
          }
        }
      }

      setStats(newStats);
      setUniqueClientTotal(nextUniqueClientTotal);
      setGlobalDueStats({
        overdue: nextGlobalOverdue,
        thisWeek: nextGlobalThisWeek,
        in2Weeks: nextGlobalIn2Weeks,
        thisMonth: nextGlobalThisMonth,
        nextMonth: nextGlobalNextMonth,
        next2Months: nextGlobalNext2Months,
      });
      setUnassignedWorkItems(nextUnassignedWorkItems);
      setStaffWorkloads(
        Array.from(nextStaffWorkloadsMap.values())
          .map((entry) => ({
            ...entry,
            moduleCount: entry.moduleMap.size,
            moduleBreakdown: Array.from(entry.moduleMap.values()).sort((left, right) => right.count - left.count),
          }))
          .sort((left, right) => {
            if (right.overdue !== left.overdue) return right.overdue - left.overdue;
            if (right.thisWeek !== left.thisWeek) return right.thisWeek - left.thisWeek;
            return right.totalAssigned - left.totalAssigned;
          })
      );
    } catch (err) {
      console.error('Error fetching dashboard stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const fallbackSummedClients = Object.values(stats).reduce((sum, s) => sum + (s.total || 0), 0);
  const totalClients = uniqueClientTotal > 0 ? uniqueClientTotal : fallbackSummedClients;
  const fallbackSummedOverdue = Object.values(stats).reduce((sum, s) => sum + (s.overdue || 0), 0);
  const fallbackSummedThisWeek = Object.values(stats).reduce((sum, s) => sum + (s.thisWeek || 0), 0);
  const fallbackSummedIn2Weeks = Object.values(stats).reduce((sum, s) => sum + (s.in2Weeks || 0), 0);
  const fallbackSummedThisMonth = Object.values(stats).reduce((sum, s) => sum + (s.thisMonth || 0), 0);
  const fallbackSummedNextMonth = Object.values(stats).reduce((sum, s) => sum + (s.nextMonth || 0), 0);
  const fallbackSummedNext2Months = Object.values(stats).reduce((sum, s) => sum + (s.next2Months || 0), 0);
  const totalOverdue = globalDueStats.overdue > 0 ? globalDueStats.overdue : fallbackSummedOverdue;
  const totalThisWeek = globalDueStats.thisWeek > 0 ? globalDueStats.thisWeek : fallbackSummedThisWeek;
  const totalIn2Weeks = globalDueStats.in2Weeks > 0 ? globalDueStats.in2Weeks : fallbackSummedIn2Weeks;
  const totalThisMonth = globalDueStats.thisMonth > 0 ? globalDueStats.thisMonth : fallbackSummedThisMonth;
  const totalNextMonth = globalDueStats.nextMonth > 0 ? globalDueStats.nextMonth : fallbackSummedNextMonth;
  const totalNext2Months = globalDueStats.next2Months > 0 ? globalDueStats.next2Months : fallbackSummedNext2Months;
  const totalOnTrack = Math.max(0, totalClients - totalOverdue);
  const totalAssignedWorkItems = staffWorkloads.reduce((sum, row) => sum + (row.totalAssigned || 0), 0);
  const totalAssignedOverdue = staffWorkloads.reduce((sum, row) => sum + (row.overdue || 0), 0);

  const openModule = (moduleId, filter = 'all', extra = {}) => {
    if (!moduleId || !onNavigate) return;
    onNavigate({ moduleId, filter, source: 'dashboard', ...extra });
  };

  const moduleStats = useMemo(() => (
    Object.entries(stats)
      .map(([key, stat]) => {
        const module = MODULES[key];
        if (!module) return null;

        return {
          key,
          moduleId: module.id,
          label: module.label,
          colorClass: module.colorClass,
          icon: module.icon,
          total: stat.total || 0,
          overdue: stat.overdue || 0,
          thisWeek: stat.thisWeek || 0,
          in2Weeks: stat.in2Weeks || 0,
          thisMonth: stat.thisMonth || 0,
          nextMonth: stat.nextMonth || 0,
          next2Months: stat.next2Months || 0,
          onTrack: Math.max(0, (stat.total || 0) - (stat.overdue || 0)),
        };
      })
      .filter(Boolean)
  ), [stats]);

  const toggleSection = (id) => setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));
  const expandAll = () => {
    const next = { topLevel: true, timeHorizon: true };
    moduleStats.forEach(item => { next[item.key] = true; });
    setExpandedSections(next);
  };
  const collapseAll = () => {
    const next = { topLevel: false, timeHorizon: false };
    moduleStats.forEach(item => { next[item.key] = false; });
    setExpandedSections(next);
  };
  const resetToDefault = () => {
    const next = { topLevel: true, timeHorizon: true };
    moduleStats.forEach(item => { next[item.key] = false; });
    setExpandedSections(next);
  };

  const baseClientModuleIds = new Set(['ch_company', 'charity', 'individual', 'unregistered', 'partnership']);

  const summaryDestinations = useMemo(() => ({
    total: moduleStats
      .filter((item) => baseClientModuleIds.has(item.moduleId) && item.total > 0)
      .sort((left, right) => right.total - left.total),
    overdue: moduleStats
      .filter((item) => item.overdue > 0)
      .sort((left, right) => right.overdue - left.overdue),
    dueThisWeek: moduleStats
      .filter((item) => item.thisWeek > 0)
      .sort((left, right) => right.thisWeek - left.thisWeek),
    dueIn2Weeks: moduleStats
      .filter((item) => item.in2Weeks > 0)
      .sort((left, right) => right.in2Weeks - left.in2Weeks),
    dueThisMonth: moduleStats
      .filter((item) => item.thisMonth > 0)
      .sort((left, right) => right.thisMonth - left.thisMonth),
    dueNextMonth: moduleStats
      .filter((item) => item.nextMonth > 0)
      .sort((left, right) => right.nextMonth - left.nextMonth),
    dueNext2Months: moduleStats
      .filter((item) => item.next2Months > 0)
      .sort((left, right) => right.next2Months - left.next2Months),
    onTrack: moduleStats
      .filter((item) => item.onTrack > 0)
      .sort((left, right) => right.onTrack - left.onTrack),
  }), [moduleStats]);

  const SummaryCard = ({ id, title, value, icon: Icon, valueClassName, iconWrapClassName, iconClassName, destinations, filter, helperText, ratio, toneClassName }) => {
    const isActive = activeSummaryCard === id;

    return (
      <Card className={`p-4 border-2 transition-all shadow-md ${toneClassName || 'bg-white border-slate-300'} ${isActive ? 'ring-2 ring-blue-300 shadow-xl' : 'hover:shadow-lg hover:-translate-y-0.5'}`}>
        <button
          type="button"
          onClick={() => setActiveSummaryCard((prev) => prev === id ? null : id)}
          className="w-full text-left"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className={`p-2 rounded-lg ${iconWrapClassName}`}>
                <Icon className={`w-6 h-6 ${iconClassName}`} />
              </div>
              <div className="min-w-0">
                <p className="text-sm text-slate-600">{title}</p>
                <p className={`text-2xl font-bold ${valueClassName}`}>{value}</p>
              </div>
            </div>
            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isActive ? 'rotate-180' : ''}`} />
          </div>
          <p className="mt-2 text-[11px] text-slate-500">{helperText}</p>
          {ratio && (
            <div className="mt-2.5">
              {ratio.stacked ? (
                <>
                  <div className="text-[10px] text-slate-400 mb-1">{ratio.label}</div>
                  <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden flex">
                    {ratio.segments.map((seg, i) => (
                      <div key={i} className={`h-full ${seg.color} transition-all duration-500`} style={{ width: `${seg.pct}%` }} />
                    ))}
                  </div>
                  <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                    {ratio.legend.map((item, i) => (
                      <span key={i} className={`flex items-center gap-1 text-[10px] ${item.textColor}`}>
                        <span className={`inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 ${item.dotColor}`} />
                        {item.label}
                      </span>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between text-[10px] mb-1">
                    <span className="text-slate-400">{ratio.label}</span>
                    <span className={ratio.textColor || 'text-slate-500'}>{ratio.pct}%</span>
                  </div>
                  <div className="h-1 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className={`h-full ${ratio.barColor} rounded-full transition-all duration-500`}
                      style={{ width: `${ratio.pct}%` }}
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </button>

        {isActive && (
          <div className="mt-3 border-t border-slate-100 pt-3 space-y-2">
            {destinations.length > 0 ? destinations.map((item) => {
              const metricValue = filter === 'overdue'
                ? item.overdue
                : filter === 'due-this-week'
                  ? item.thisWeek
                  : filter === 'due-in-2-weeks'
                    ? item.in2Weeks
                    : filter === 'due-this-month'
                      ? item.thisMonth
                      : filter === 'due-next-month'
                        ? item.nextMonth
                        : filter === 'due-next-2-months'
                          ? item.next2Months
                  : filter === 'on-track'
                    ? item.onTrack
                    : item.total;

              return (
                <button
                  key={`${id}-${item.moduleId}`}
                  type="button"
                  onClick={() => openModule(item.moduleId, filter)}
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-left hover:bg-slate-100 transition-colors flex items-center justify-between gap-2"
                >
                  <span className="text-sm font-medium text-slate-700 truncate">{item.label}</span>
                  <Badge variant={filter === 'overdue' ? 'danger' : filter === 'due-this-week' ? 'warning' : filter === 'on-track' ? 'success' : 'secondary'}>{metricValue}</Badge>
                </button>
              );
            }) : (
              <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">
                No matching tabs currently need attention.
              </div>
            )}
          </div>
        )}
      </Card>
    );
  };

  const overdueRatio = totalClients > 0 ? Math.round((totalOverdue / totalClients) * 100) : 0;
  const thisWeekRatio = totalClients > 0 ? Math.round((totalThisWeek / totalClients) * 100) : 0;
  const onTrackRatio  = totalClients > 0 ? Math.round((totalOnTrack  / totalClients) * 100) : 0;

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 pb-6 pt-2 space-y-4">
      <div className="flex items-center justify-end">
        <div className="flex items-center gap-2">
          <Button onClick={resetToDefault} variant="outline" size="sm">Default</Button>
          <Button onClick={collapseAll} variant="outline" size="sm">Collapse All</Button>
          <Button onClick={expandAll} variant="outline" size="sm">Expand All</Button>
          <Button onClick={fetchDashboardStats} variant="outline" size="sm">
            <BarChart3 className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      <Card className="border-2 border-slate-500 bg-slate-100 shadow-xl">
        <button
          type="button"
          onClick={() => toggleSection('topLevel')}
          className="w-full flex items-center justify-between gap-3 p-4"
        >
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-600">Top Level Stats</h2>
          <div className="flex items-center gap-2">
            {!expandedSections.topLevel && (
              <>
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-300 text-slate-700 font-medium">{totalClients} clients</span>
                {totalOverdue > 0 && <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-200 text-red-700 font-medium">{totalOverdue} overdue</span>}
                {totalThisWeek > 0 && <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-200 text-amber-700 font-medium">{totalThisWeek} this week</span>}
              </>
            )}
            <ChevronDown className={`w-5 h-5 text-slate-500 flex-shrink-0 transition-transform duration-200 ${expandedSections.topLevel ? 'rotate-180' : ''}`} />
          </div>
        </button>
        {expandedSections.topLevel && (
          <div className="px-4 pb-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <SummaryCard
            id="total"
            title="Total Clients"
            value={totalClients}
            toneClassName="bg-slate-200 border-slate-400"
            icon={Users}
            valueClassName="text-slate-800"
            iconWrapClassName="bg-blue-200"
            iconClassName="text-blue-700"
            destinations={summaryDestinations.total}
            filter="all"
            helperText="Choose a base client tab to open the underlying client population."
            ratio={{
              stacked: true,
              label: 'Health overview',
              segments: [
                { pct: overdueRatio, color: 'bg-red-500' },
                { pct: thisWeekRatio, color: 'bg-amber-500' },
                { pct: Math.max(0, 100 - overdueRatio - thisWeekRatio), color: 'bg-green-500' },
              ],
              legend: [
                { label: `${overdueRatio}% overdue`,   dotColor: 'bg-red-500',   textColor: 'text-red-600' },
                { label: `${thisWeekRatio}% this week`, dotColor: 'bg-amber-500', textColor: 'text-amber-700' },
                { label: `${onTrackRatio}% on track`,   dotColor: 'bg-green-500', textColor: 'text-green-700' },
              ],
            }}
          />

          <SummaryCard
            id="overdue"
            title="Overdue"
            value={totalOverdue}
            toneClassName="bg-rose-100 border-rose-400"
            icon={AlertTriangle}
            valueClassName="text-red-700"
            iconWrapClassName="bg-red-200"
            iconClassName="text-red-700"
            destinations={summaryDestinations.overdue}
            filter="overdue"
            helperText="Open the specific module already filtered to overdue work."
            ratio={{ label: 'of all clients', pct: overdueRatio, barColor: 'bg-red-500', textColor: 'text-red-600' }}
          />

          <SummaryCard
            id="due-this-week"
            title="Due This Week"
            value={totalThisWeek}
            toneClassName="bg-amber-100 border-amber-400"
            icon={Clock}
            valueClassName="text-amber-700"
            iconWrapClassName="bg-amber-200"
            iconClassName="text-amber-700"
            destinations={summaryDestinations.dueThisWeek}
            filter="due-this-week"
            helperText="Jump straight into module views for work due in the next 7 days."
            ratio={{ label: 'of all clients', pct: thisWeekRatio, barColor: 'bg-amber-500', textColor: 'text-amber-700' }}
          />

          <SummaryCard
            id="on-track"
            title="On Track"
            value={totalOnTrack}
            toneClassName="bg-emerald-100 border-emerald-400"
            icon={CheckCircle}
            valueClassName="text-green-700"
            iconWrapClassName="bg-green-200"
            iconClassName="text-green-700"
            destinations={summaryDestinations.onTrack}
            filter="on-track"
            helperText="Open modules showing clients that currently have no overdue due dates."
            ratio={{ label: 'of all clients', pct: onTrackRatio, barColor: 'bg-green-500', textColor: 'text-green-700' }}
          />
          </div>
        )}
      </Card>

      <Card className="border-2 border-indigo-500 bg-indigo-100 shadow-xl">
        <button
          type="button"
          onClick={() => toggleSection('timeHorizon')}
          className="w-full flex items-center justify-between gap-3 p-4"
        >
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-600">Time Horizon Stats</h2>
          <div className="flex items-center gap-2">
            {!expandedSections.timeHorizon && (
              <>
                {totalIn2Weeks > 0 && <span className="text-[11px] px-2 py-0.5 rounded-full bg-orange-200 text-orange-700 font-medium">{totalIn2Weeks} in 2 wks</span>}
                {totalThisMonth > 0 && <span className="text-[11px] px-2 py-0.5 rounded-full bg-purple-200 text-purple-700 font-medium">{totalThisMonth} this mth</span>}
                {totalNextMonth > 0 && <span className="text-[11px] px-2 py-0.5 rounded-full bg-indigo-200 text-indigo-700 font-medium">{totalNextMonth} next mth</span>}
              </>
            )}
            <ChevronDown className={`w-5 h-5 text-slate-500 flex-shrink-0 transition-transform duration-200 ${expandedSections.timeHorizon ? 'rotate-180' : ''}`} />
          </div>
        </button>
        {expandedSections.timeHorizon && (
          <div className="px-4 pb-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <SummaryCard
            id="due-in-2-weeks"
            title="Due in 2 Weeks"
            value={totalIn2Weeks}
            toneClassName="bg-orange-100 border-orange-400"
            icon={Clock}
            valueClassName="text-orange-700"
            iconWrapClassName="bg-orange-200"
            iconClassName="text-orange-700"
            destinations={summaryDestinations.dueIn2Weeks}
            filter="due-in-2-weeks"
            helperText="Due after this Friday and by next Friday."
            ratio={{ label: 'of all clients', pct: totalClients > 0 ? Math.round((totalIn2Weeks / totalClients) * 100) : 0, barColor: 'bg-orange-500', textColor: 'text-orange-700' }}
          />

          <SummaryCard
            id="due-this-month"
            title="Due This Month"
            value={totalThisMonth}
            toneClassName="bg-purple-100 border-purple-400"
            icon={Clock}
            valueClassName="text-purple-700"
            iconWrapClassName="bg-purple-200"
            iconClassName="text-purple-700"
            destinations={summaryDestinations.dueThisMonth}
            filter="due-this-month"
            helperText="Calendar month from today to month-end."
            ratio={{ label: 'of all clients', pct: totalClients > 0 ? Math.round((totalThisMonth / totalClients) * 100) : 0, barColor: 'bg-purple-500', textColor: 'text-purple-700' }}
          />

          <SummaryCard
            id="due-next-month"
            title="Due Next Month"
            value={totalNextMonth}
            toneClassName="bg-indigo-100 border-indigo-400"
            icon={Clock}
            valueClassName="text-indigo-700"
            iconWrapClassName="bg-indigo-200"
            iconClassName="text-indigo-700"
            destinations={summaryDestinations.dueNextMonth}
            filter="due-next-month"
            helperText="Upcoming next calendar month only."
            ratio={{ label: 'of all clients', pct: totalClients > 0 ? Math.round((totalNextMonth / totalClients) * 100) : 0, barColor: 'bg-indigo-500', textColor: 'text-indigo-700' }}
          />

          <SummaryCard
            id="due-next-2-months"
            title="Due in 2 Months"
            value={totalNext2Months}
            toneClassName="bg-cyan-100 border-cyan-400"
            icon={Clock}
            valueClassName="text-cyan-700"
            iconWrapClassName="bg-cyan-200"
            iconClassName="text-cyan-700"
            destinations={summaryDestinations.dueNext2Months}
            filter="due-next-2-months"
            helperText="Month after next only (calendar month)."
            ratio={{ label: 'of all clients', pct: totalClients > 0 ? Math.round((totalNext2Months / totalClients) * 100) : 0, barColor: 'bg-cyan-500', textColor: 'text-cyan-700' }}
          />

          </div>
        )}
      </Card>

      {/* Module Breakdown — sorted by attention (overdue ↓, this week ↓, total ↓), collapsible */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {[...moduleStats]
          .sort((a, b) => {
            if (b.overdue !== a.overdue) return b.overdue - a.overdue;
            if (b.thisWeek !== a.thisWeek) return b.thisWeek - a.thisWeek;
            return b.total - a.total;
          })
          .map((item, index) => {
            const module = MODULES[item.key];
            if (!module) return null;
            const moduleToneClasses = [
              'bg-slate-100 border-slate-500',
              'bg-blue-100 border-blue-500',
              'bg-emerald-100 border-emerald-500',
              'bg-amber-100 border-amber-500',
              'bg-violet-100 border-violet-500',
              'bg-cyan-100 border-cyan-500',
              'bg-rose-100 border-rose-500',
              'bg-indigo-100 border-indigo-500',
            ];
            const moduleTone = moduleToneClasses[index % moduleToneClasses.length];
            const Icon = item.icon || module.icon;
            const moduleId = item.moduleId;
            const moduleTotal = item.total;
            const moduleOverdue = item.overdue;
            const moduleThisWeek = item.thisWeek;
            const moduleOnTrack = item.onTrack;
            const moduleOverdueRatio = moduleTotal > 0 ? Math.round((moduleOverdue / moduleTotal) * 100) : 0;
            const moduleThisWeekRatio = moduleTotal > 0 ? Math.round((moduleThisWeek / moduleTotal) * 100) : 0;
            const moduleOnTrackRatio = moduleTotal > 0 ? Math.round((moduleOnTrack / moduleTotal) * 100) : 0;
            const isExpanded = Boolean(expandedSections[item.key]);

            return (
              <Card key={item.key} className={`border-2 shadow-lg hover:shadow-xl transition-shadow ${moduleTone}`}>
                {/* Always-visible toggle header */}
                <button
                  type="button"
                  onClick={() => toggleSection(item.key)}
                  className="w-full flex items-center justify-between gap-2 p-4"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`p-2 rounded-lg flex-shrink-0 ${module.colorClass}`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="font-semibold text-slate-800 truncate">{module.label}</h3>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/70 text-slate-700 font-medium border border-slate-300">{moduleTotal}</span>
                    {moduleOverdue > 0 && (
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium border border-red-300">{moduleOverdue} overdue</span>
                    )}
                    {!isExpanded && moduleThisWeek > 0 && (
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium border border-amber-300">{moduleThisWeek} this wk</span>
                    )}
                    <ChevronDown className={`w-4 h-4 text-slate-500 ml-1 flex-shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                  </div>
                </button>

                {/* Expanded content */}
                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-white/50 pt-3">
                    <div className="flex items-center justify-end mb-3">
                      <Button variant="outline" size="sm" onClick={() => openModule(moduleId, 'all')}>
                        Open Tab
                      </Button>
                    </div>

                    <div className="grid grid-cols-3 gap-2">
                      <button
                        type="button"
                        onClick={() => openModule(moduleId, 'all')}
                        className="rounded-lg border border-slate-200 bg-slate-50 px-2 py-2 text-left hover:bg-slate-100 transition-colors"
                      >
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Total</div>
                        <div className="mt-1 mb-1.5"><Badge variant="secondary">{moduleTotal}</Badge></div>
                        <div className="text-[10px] text-slate-500">On track {moduleOnTrackRatio}%</div>
                        <div className="mt-1 h-1 rounded-full bg-slate-200 overflow-hidden">
                          <div className="h-full bg-green-400 rounded-full" style={{ width: `${moduleOnTrackRatio}%` }} />
                        </div>
                      </button>

                      <button
                        type="button"
                        onClick={() => openModule(moduleId, 'overdue')}
                        className="rounded-lg border border-rose-200 bg-rose-50 px-2 py-2 text-left hover:bg-rose-100 transition-colors"
                      >
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-rose-600">Overdue</div>
                        <div className="mt-1 mb-1.5"><Badge variant="danger">{moduleOverdue}</Badge></div>
                        <div className="text-[10px] text-rose-600">{moduleOverdueRatio}% of module</div>
                        <div className="mt-1 h-1 rounded-full bg-rose-200 overflow-hidden">
                          <div className="h-full bg-red-400 rounded-full" style={{ width: `${moduleOverdueRatio}%` }} />
                        </div>
                      </button>

                      <button
                        type="button"
                        onClick={() => openModule(moduleId, 'due-this-week')}
                        className="rounded-lg border border-amber-200 bg-amber-50 px-2 py-2 text-left hover:bg-amber-100 transition-colors"
                      >
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-amber-700">This Week</div>
                        <div className="mt-1 mb-1.5"><Badge variant="warning">{moduleThisWeek}</Badge></div>
                        <div className="text-[10px] text-amber-700">{moduleThisWeekRatio}% of module</div>
                        <div className="mt-1 h-1 rounded-full bg-amber-200 overflow-hidden">
                          <div className="h-full bg-amber-400 rounded-full" style={{ width: `${moduleThisWeekRatio}%` }} />
                        </div>
                      </button>
                    </div>

                    <div className="grid grid-cols-4 gap-2 mt-2">
                      <button
                        type="button"
                        onClick={() => openModule(moduleId, 'due-in-2-weeks')}
                        className="rounded-lg border border-orange-200 bg-orange-50 px-2 py-2 text-left hover:bg-orange-100 transition-colors"
                      >
                        <div className="text-[10px] font-semibold uppercase tracking-wide text-orange-700">2 Weeks</div>
                        <div className="mt-1"><Badge variant="warning">{item.in2Weeks || 0}</Badge></div>
                      </button>

                      <button
                        type="button"
                        onClick={() => openModule(moduleId, 'due-this-month')}
                        className="rounded-lg border border-purple-200 bg-purple-50 px-2 py-2 text-left hover:bg-purple-100 transition-colors"
                      >
                        <div className="text-[10px] font-semibold uppercase tracking-wide text-purple-700">This Month</div>
                        <div className="mt-1"><Badge variant="secondary">{item.thisMonth || 0}</Badge></div>
                      </button>

                      <button
                        type="button"
                        onClick={() => openModule(moduleId, 'due-next-month')}
                        className="rounded-lg border border-indigo-200 bg-indigo-50 px-2 py-2 text-left hover:bg-indigo-100 transition-colors"
                      >
                        <div className="text-[10px] font-semibold uppercase tracking-wide text-indigo-700">Next Month</div>
                        <div className="mt-1"><Badge variant="secondary">{item.nextMonth || 0}</Badge></div>
                      </button>

                      <button
                        type="button"
                        onClick={() => openModule(moduleId, 'due-next-2-months')}
                        className="rounded-lg border border-cyan-200 bg-cyan-50 px-2 py-2 text-left hover:bg-cyan-100 transition-colors"
                      >
                        <div className="text-[10px] font-semibold uppercase tracking-wide text-cyan-700">In 2 Months</div>
                        <div className="mt-1"><Badge variant="secondary">{item.next2Months || 0}</Badge></div>
                      </button>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
      </div>

    </div>
  );
};

export default Dashboard;